import React from 'react';
import { prop, map, addIndex, filter, hasPath, compose } from 'ramda';
import { differenceInDays, format as dateFormat } from 'date-fns';
import { format } from 'd3-format';
import { Merchant } from 'hooks/useMerchants';

import { ISO_Format_Date2, parseDate } from 'helpers/date';
import { sortBy } from 'helpers/safeRamda';

import ProtectedRoute from 'components/ProtectedRoute';
import { Tabs } from 'components/TabbedMenu/TabbedMenu.helper';

export type Meta = {
  showForOnline: boolean;
  thirdPartyReport: boolean;
  showForOnlineOnly: boolean;
  type: string;
  border?: boolean;
  batchNotAllowed?: boolean;
  merchantList?: string[];
};

export type Report = {
  key: string;
  text: string;
  value: string;
  image: string;
  meta: Meta;
};

export type Scale = {
  x: string | number;
  y: number;
  date?: Date;
};

enum Timeslice {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
}

const mapIndexed = addIndex<any>(map);

const isValidReport = (
  { showForOnline, showForOnlineOnly }: Meta,
  merchant: Merchant,
): boolean => {
  if (!merchant) {
    return false;
  }

  // Merchant has ONLY online venues and the report is not for online merchants
  if (merchant.onlineOnly && !showForOnline) {
    return false;
  }

  // Report is ONLY for online merchants, but the merchant does not have online venues
  if (showForOnlineOnly && !merchant.hasOnlineVenue) {
    return false;
  }

  return true;
};

const tickFormatYAxis = (value: number): string => format('$,')(value);

const sortMonthTransformer = (fieldName: string) => sortBy(prop(fieldName));

const getTickFormat = (
  timeslice: Timeslice,
  value: string,
  scale?: Scale,
): { top: string; bottom: string } => {
  if (timeslice === Timeslice.WEEKLY) {
    if (scale && scale.date) {
      return {
        top: dateFormat(scale.date, 'dd'),
        bottom: dateFormat(scale.date, 'MMM'),
      };
    }
  }

  const date = parseDate(value, ISO_Format_Date2) ?? scale?.date;
  if (!date) {
    throw new Error('Invalid date format');
  }

  if (timeslice === Timeslice.MONTHLY) {
    return {
      top: dateFormat(date, 'MMM'),
      bottom: dateFormat(date, 'yyyy'),
    };
  }

  if (timeslice === Timeslice.QUARTERLY) {
    return {
      top: dateFormat(date, 'Qo') + ' Quarter',
      bottom: '',
    };
  }

  return {
    top: dateFormat(date, 'dd'),
    bottom: dateFormat(date, 'MMM'),
  };
};

const getAllReports = compose(
  map(prop('key')) as any,
  filter((report: Report) => !hasPath(['meta', 'batchNotAllowed'])(report)),
) as any;

const getUserIdentityId = prop('identityId');

const merchantTransformer = map(
  ({ merchantName, offerNetworkExternalVenueId }: Merchant) => ({
    key: offerNetworkExternalVenueId,
    value: JSON.stringify({
      merchantName,
      merchantId: offerNetworkExternalVenueId,
    }),
    text: merchantName,
  }),
);

const DAILY_METRIC_PERIOD = 30;
const WEEKLY_METRIC_PERIOD = 120;
const QUARTERLY_PERIOD = 365;

const getTimesliceFromPeriod = (startDate: Date, endDate: Date): Timeslice => {
  if (startDate && endDate) {
    const daysDiff = differenceInDays(endDate, startDate);

    if (daysDiff <= DAILY_METRIC_PERIOD) {
      return Timeslice.DAILY;
    }

    if (daysDiff <= WEEKLY_METRIC_PERIOD) {
      return Timeslice.WEEKLY;
    }

    if (daysDiff >= QUARTERLY_PERIOD) {
      return Timeslice.QUARTERLY;
    }
  }

  return Timeslice.MONTHLY;
};

const renderRoutesMapping = (whitelistReports?: string[]) => (
  options: Record<string, unknown>,
  index: number,
) => {
  if (
    !whitelistReports ||
    whitelistReports.includes((options as Tabs).path.split('/').pop() as string)
  ) {
    return <ProtectedRoute key={index} {...options} />;
  }

  return null;
};

const renderRoutes = (
  routes: Record<string, unknown>[],
  whitelistReports?: string[],
): any => mapIndexed(renderRoutesMapping(whitelistReports), routes);

export {
  isValidReport,
  tickFormatYAxis,
  sortMonthTransformer,
  getTickFormat,
  merchantTransformer,
  getAllReports,
  getUserIdentityId,
  getTimesliceFromPeriod,
  renderRoutes,
  Timeslice,
};
